import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader2, TrendingUp, Music, Heart, Plus, Headphones, Save, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config';

const API = API_URL;

export const PlaylistAnalyzer = ({ onClose, onSongsUpdated }) => {
  const [playlistUrls, setPlaylistUrls] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [addingToLibrary, setAddingToLibrary] = useState({});
  const [addingToDenon, setAddingToDenon] = useState({});
  const [overwriteRatings, setOverwriteRatings] = useState(false);
  const [minOccurrences, setMinOccurrences] = useState(1);
  const [selectedSongs, setSelectedSongs] = useState(new Set());
  const [addingAllToLibrary, setAddingAllToLibrary] = useState(false);
  const [addingAllToDenon, setAddingAllToDenon] = useState(false);
  const [prototypeName, setPrototypeName] = useState('');
  const [isCreatingPrototype, setIsCreatingPrototype] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);
  // IDs de canciones creadas como "pendiente Denon" durante esta sesión del
  // Analyzer. Se usa para, opcionalmente, incluirlas en el evento-prototipo.
  const [addedDenonIds, setAddedDenonIds] = useState(new Set());
  const [prototypeIncludeDenon, setPrototypeIncludeDenon] = useState(true);

  const handleAnalyze = async () => {
    // Parse URLs (one per line)
    const urls = playlistUrls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url && url.includes('spotify.com/playlist'));

    if (urls.length === 0) {
      toast.error('Por favor, pega al menos una URL de playlist de Spotify');
      return;
    }

    if (urls.length > 20) {
      toast.error('Máximo 20 playlists por análisis');
      return;
    }

    try {
      setIsAnalyzing(true);
      toast.info(`🔍 Analizando ${urls.length} playlist${urls.length > 1 ? 's' : ''}...`);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/songs/analyze-playlists`,
        urls,
        {
          params: {
            overwrite_ratings: overwriteRatings
          },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setResults(response.data);
      toast.success(`✅ Análisis completado: ${response.data.songs_updated} canciones actualizadas`);
      
      if (onSongsUpdated) {
        onSongsUpdated();
      }
    } catch (error) {
      console.error('Error analyzing playlists:', error);
      toast.error('Error al analizar playlists: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAddToLibrary = async (song) => {
    try {
      setAddingToLibrary(prev => ({ ...prev, [song.title]: true }));
      
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/songs`,
        {
          title: song.title,
          artist: song.artist,
          rating: song.rating,
          favorito: song.favorito,
          spotify_url: song.spotify_url,
          spotify_uri: song.spotify_uri
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast.success(`✅ "${song.title}" añadida a biblioteca`);
      
      // Remove from recommendations
      setResults(prev => ({
        ...prev,
        recommended_songs: prev.recommended_songs.filter(s => s.title !== song.title),
        recommendations_count: prev.recommendations_count - 1
      }));
      
      if (onSongsUpdated) {
        onSongsUpdated();
      }
    } catch (error) {
      console.error('Error adding song:', error);
      toast.error('Error al añadir canción');
    } finally {
      setAddingToLibrary(prev => ({ ...prev, [song.title]: false }));
    }
  };

  const handleAddToDenon = async (song) => {
    try {
      setAddingToDenon(prev => ({ ...prev, [song.title]: true }));
      
      const token = localStorage.getItem('token');
      const { data: created } = await axios.post(
        `${API}/songs`,
        {
          title: song.title,
          artist: song.artist,
          rating: song.rating,
          favorito: song.favorito,
          spotify_id: song.spotify_id,
          spotify_url: song.spotify_url,
          spotify_uri: song.spotify_uri,
          pendiente_denon: true
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (created?.id) {
        setAddedDenonIds(prev => {
          const next = new Set(prev);
          next.add(created.id);
          return next;
        });
      }
      toast.success(`✅ "${song.title}" añadida como pendiente Denon`);
      
      // Remove from recommendations
      setResults(prev => ({
        ...prev,
        recommended_songs: prev.recommended_songs.filter(s => s.title !== song.title),
        recommendations_count: prev.recommendations_count - 1
      }));
      
      if (onSongsUpdated) {
        onSongsUpdated();
      }
    } catch (error) {
      console.error('Error adding song to Denon:', error);
      toast.error('Error al añadir a Denon');
    } finally {
      setAddingToDenon(prev => ({ ...prev, [song.title]: false }));
    }
  };

  const handleAddAllToLibrary = async () => {
    const songsToAdd = filteredRecommendations;
    
    if (songsToAdd.length === 0) {
      toast.error('No hay canciones para añadir');
      return;
    }

    const confirmed = window.confirm(
      `¿Añadir ${songsToAdd.length} canción${songsToAdd.length > 1 ? 'es' : ''} a la biblioteca?`
    );
    
    if (!confirmed) return;

    try {
      setAddingAllToLibrary(true);
      const token = localStorage.getItem('token');
      let added = 0;

      for (const song of songsToAdd) {
        try {
          await axios.post(
            `${API}/songs`,
            {
              title: song.title,
              artist: song.artist,
              rating: song.rating,
              favorito: song.favorito,
              spotify_url: song.spotify_url,
              spotify_uri: song.spotify_uri
            },
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          added++;
        } catch (error) {
          console.error(`Error adding ${song.title}:`, error);
        }
      }

      toast.success(`✅ ${added} canción${added > 1 ? 'es' : ''} añadida${added > 1 ? 's' : ''} a biblioteca`);
      
      setResults(prev => ({
        ...prev,
        recommended_songs: [],
        recommendations_count: 0
      }));
      
      if (onSongsUpdated) {
        onSongsUpdated();
      }
    } catch (error) {
      toast.error('Error al añadir canciones');
    } finally {
      setAddingAllToLibrary(false);
    }
  };

  const handleAddAllToDenon = async () => {
    const songsToAdd = filteredRecommendations;
    
    if (songsToAdd.length === 0) {
      toast.error('No hay canciones para añadir');
      return;
    }

    const confirmed = window.confirm(
      `¿Añadir ${songsToAdd.length} canción${songsToAdd.length > 1 ? 'es' : ''} como pendientes Denon?`
    );
    
    if (!confirmed) return;

    try {
      setAddingAllToDenon(true);
      const token = localStorage.getItem('token');
      let added = 0;
      const newIds = [];

      for (const song of songsToAdd) {
        try {
          const { data: created } = await axios.post(
            `${API}/songs`,
            {
              title: song.title,
              artist: song.artist,
              rating: song.rating,
              favorito: song.favorito,
              spotify_id: song.spotify_id,
              spotify_url: song.spotify_url,
              spotify_uri: song.spotify_uri,
              pendiente_denon: true
            },
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          if (created?.id) newIds.push(created.id);
          added++;
        } catch (error) {
          console.error(`Error adding ${song.title}:`, error);
        }
      }

      if (newIds.length) {
        setAddedDenonIds(prev => {
          const next = new Set(prev);
          newIds.forEach(id => next.add(id));
          return next;
        });
      }
      toast.success(`✅ ${added} canción${added > 1 ? 'es' : ''} añadida${added > 1 ? 's' : ''} como pendientes Denon`);
      
      setResults(prev => ({
        ...prev,
        recommended_songs: [],
        recommendations_count: 0
      }));
      
      if (onSongsUpdated) {
        onSongsUpdated();
      }
    } catch (error) {
      toast.error('Error al añadir canciones');
    } finally {
      setAddingAllToDenon(false);
    }
  };

  const handleAddSelectedToLibrary = async () => {
    if (selectedSongs.size === 0) {
      toast.error('Selecciona al menos una canción');
      return;
    }

    const songsToAdd = filteredRecommendations.filter((_, index) => selectedSongs.has(index));
    
    try {
      const token = localStorage.getItem('token');
      let added = 0;

      for (const song of songsToAdd) {
        try {
          await axios.post(
            `${API}/songs`,
            {
              title: song.title,
              artist: song.artist,
              rating: song.rating,
              favorito: song.favorito,
              spotify_url: song.spotify_url,
              spotify_uri: song.spotify_uri
            },
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          added++;
        } catch (error) {
          console.error(`Error adding ${song.title}:`, error);
        }
      }

      toast.success(`✅ ${added} canción${added > 1 ? 'es' : ''} añadida${added > 1 ? 's' : ''}`);
      
      // Remove added songs from recommendations
      setResults(prev => ({
        ...prev,
        recommended_songs: prev.recommended_songs.filter(s => !songsToAdd.some(sa => sa.title === s.title)),
        recommendations_count: prev.recommendations_count - added
      }));
      
      setSelectedSongs(new Set());
      
      if (onSongsUpdated) {
        onSongsUpdated();
      }
    } catch (error) {
      toast.error('Error al añadir canciones');
    }
  };

  const handleAddSelectedToDenon = async () => {
    if (selectedSongs.size === 0) {
      toast.error('Selecciona al menos una canción');
      return;
    }

    const songsToAdd = filteredRecommendations.filter((_, index) => selectedSongs.has(index));
    
    try {
      const token = localStorage.getItem('token');
      let added = 0;
      const newIds = [];

      for (const song of songsToAdd) {
        try {
          const { data: created } = await axios.post(
            `${API}/songs`,
            {
              title: song.title,
              artist: song.artist,
              rating: song.rating,
              favorito: song.favorito,
              spotify_id: song.spotify_id,
              spotify_url: song.spotify_url,
              spotify_uri: song.spotify_uri,
              pendiente_denon: true
            },
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          if (created?.id) newIds.push(created.id);
          added++;
        } catch (error) {
          console.error(`Error adding ${song.title}:`, error);
        }
      }

      if (newIds.length) {
        setAddedDenonIds(prev => {
          const next = new Set(prev);
          newIds.forEach(id => next.add(id));
          return next;
        });
      }
      toast.success(`✅ ${added} canción${added > 1 ? 'es' : ''} añadida${added > 1 ? 's' : ''} a Denon`);
      
      // Remove added songs from recommendations
      setResults(prev => ({
        ...prev,
        recommended_songs: prev.recommended_songs.filter(s => !songsToAdd.some(sa => sa.title === s.title)),
        recommendations_count: prev.recommendations_count - added
      }));
      
      setSelectedSongs(new Set());
      
      if (onSongsUpdated) {
        onSongsUpdated();
      }
    } catch (error) {
      toast.error('Error al añadir canciones');
    }
  };

  const toggleSongSelection = (index) => {
    setSelectedSongs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedSongs(new Set(filteredRecommendations.map((_, index) => index)));
  };

  const deselectAll = () => {
    setSelectedSongs(new Set());
  };

  const filteredRecommendations = results?.recommended_songs?.filter(
    song => song.occurrences >= minOccurrences
  ) || [];

  // Canciones ya en biblioteca que aparecen en las playlists analizadas.
  // Mismo filtro minOccurrences para trabajar como "prototipo de playlist".
  const filteredExisting = results?.existing_songs?.filter(
    song => song.occurrences >= minOccurrences
  ) || [];

  const handleToggleDenonPending = async (song) => {
    const nextVal = !song.pendiente_denon;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/songs/${song.id}`,
        { pendiente_denon: nextVal },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setResults(prev => ({
        ...prev,
        existing_songs: prev.existing_songs.map(s =>
          s.id === song.id ? { ...s, pendiente_denon: nextVal } : s
        ),
      }));
      toast.success(nextVal
        ? `✓ "${song.title}" marcada pendiente Denon`
        : `↺ "${song.title}" desmarcada de pendientes`);
      if (onSongsUpdated) onSongsUpdated();
    } catch (err) {
      console.error('Error toggling pendiente_denon:', err);
      toast.error('No se pudo actualizar el estado Denon');
    }
  };

  // One-shot admin action: rellena `spotify_id` en canciones existentes que
  // solo tenían `spotify_url`. Debe ejecutarse UNA VEZ tras el deploy para
  // habilitar el fast-path de matching por spotify_id. Idempotente.
  const handleBackfillSpotifyIds = async () => {
    if (!window.confirm(
      '¿Optimizar el matching de tu biblioteca?\n\n' +
      'Esto rellena el ID canónico de Spotify en las canciones que solo tienen URL. ' +
      'Mejora la precisión del análisis de playlists. Es seguro ejecutarlo varias veces.'
    )) return;
    try {
      setIsBackfilling(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/songs/backfill-spotify-ids`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(
        `✅ ${data.updated} canciones optimizadas` +
        (data.skipped_without_id ? ` · ${data.skipped_without_id} sin ID extraíble` : '') +
        (data.errors ? ` · ⚠️ ${data.errors} con datos corruptos (saltadas)` : '')
      );
    } catch (err) {
      console.error('Error backfilling spotify_ids:', err);
      toast.error(err.response?.data?.detail || 'Error en la optimización');
    } finally {
      setIsBackfilling(false);
    }
  };

  // Crea un evento-plantilla con las canciones de biblioteca del filtro actual.
  // Caso de uso del usuario: analiza 16 playlists de "comuniones" → filtra "3+ listas"
  // → un click crea el prototipo listo para clonar a eventos reales.
  const handleCreatePrototype = async () => {
    const name = (prototypeName || '').trim();
    if (!name) {
      toast.error('Pon un nombre al prototipo (ej. "Plantilla: Comuniones")');
      return;
    }
    // Combinar canciones de biblioteca filtradas + (opcional) recomendadas
    // añadidas como "pendiente Denon" durante esta sesión. Dedupe por si el
    // usuario añadió una recomendada que ya estaba en biblioteca (edge-case).
    const librarySongIds = filteredExisting.map(s => s.id);
    const denonIds = prototypeIncludeDenon ? Array.from(addedDenonIds) : [];
    const song_ids = Array.from(new Set([...librarySongIds, ...denonIds]));

    if (song_ids.length === 0) {
      toast.error('No hay canciones para el prototipo con el filtro actual');
      return;
    }
    try {
      setIsCreatingPrototype(true);
      const token = localStorage.getItem('token');
      const notesParts = [];
      if (denonIds.length) {
        notesParts.push(`Incluye ${denonIds.length} recomendada(s) marcada(s) como pendiente Denon.`);
      }
      const descripcion = [
        `Generada desde Analizador de Playlists (${song_ids.length} canciones, filtro ≥${minOccurrences} listas).`,
        ...notesParts,
      ].join(' ');
      const { data: created } = await axios.post(
        `${API}/playlist-templates/json`,
        {
          nombre: name,
          descripcion,
          song_ids,
          tipo_evento: '',
          tags: [],
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Toast con acción rápida para ir a Plantillas.
      toast.success(`✅ Plantilla "${created.nombre}" creada con ${song_ids.length} canciones`, {
        duration: 6000,
        action: {
          label: 'Ver Plantillas',
          onClick: () => {
            try { window.location.href = '/plantillas'; }
            catch (e) { /* noop */ }
          },
        },
      });
      setPrototypeName('');
    } catch (err) {
      console.error('Error creating prototype:', err);
      toast.error(err.response?.data?.detail || 'Error al crear el prototipo');
    } finally {
      setIsCreatingPrototype(false);
    }
  };

  const getRatingStars = (rating) => {
    return '⭐'.repeat(rating || 0);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="bg-slate-800 border-slate-700 max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="border-b border-slate-700">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-purple-400" />
              Análisis de Playlists
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleBackfillSpotifyIds}
                disabled={isBackfilling}
                variant="outline"
                size="sm"
                className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30 text-xs"
                title="Rellena el ID canónico de Spotify en canciones que solo tienen URL. Mejora el matching entre playlists. Ejecutar una vez tras cada deploy es suficiente."
                data-testid="analyzer-backfill-btn"
              >
                {isBackfilling ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                ) : (
                  <Zap className="w-3.5 h-3.5 mr-1" />
                )}
                Optimizar matching
              </Button>
              <Button
                onClick={onClose}
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                ✕
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6 overflow-auto flex-1">
          {!results ? (
            <div className="space-y-4">
              <div>
                <label className="text-white font-medium mb-2 block">
                  URLs de Playlists de Spotify (una por línea)
                </label>
                <textarea
                  value={playlistUrls}
                  onChange={(e) => setPlaylistUrls(e.target.value)}
                  placeholder="https://open.spotify.com/playlist/37i9dQZF1DXcBWIGoYBM5M&#10;https://open.spotify.com/playlist/37i9dQZF1DX0XUsuxWHRQd&#10;..."
                  className="w-full h-64 bg-slate-700 text-white rounded-lg p-3 border border-slate-600 focus:border-purple-500 focus:outline-none resize-none"
                  disabled={isAnalyzing}
                />
                <p className="text-slate-400 text-sm mt-2">
                  💡 Pega hasta 20 URLs de playlists de Spotify. El sistema:
                </p>
                <ul className="text-slate-400 text-sm mt-2 space-y-1 ml-4">
                  <li>• Actualizará valoraciones de canciones existentes en tu biblioteca</li>
                  <li>• 1 lista = 1⭐ | 2 listas = 2⭐ | 3 listas = 3⭐ | 4 listas = 4⭐ | 5 listas = 5⭐ | 6+ listas = 5⭐ + ❤️ Favorita</li>
                  <li>• Mostrará SOLO canciones nuevas que no están en tu biblioteca</li>
                </ul>
              </div>

              <div className="flex items-center gap-2 bg-slate-700 p-3 rounded-lg">
                <input
                  type="checkbox"
                  id="overwriteRatings"
                  checked={overwriteRatings}
                  onChange={(e) => setOverwriteRatings(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600"
                  disabled={isAnalyzing}
                />
                <label htmlFor="overwriteRatings" className="text-white text-sm cursor-pointer">
                  Sobrescribir valoraciones existentes (actualizar TODAS las canciones, no solo sin valoración)
                </label>
              </div>

              <Button
                onClick={handleAnalyze}
                disabled={isAnalyzing}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analizando playlists...
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Analizar Playlists
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-slate-700 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Playlists</p>
                  <p className="text-2xl font-bold text-white">{results.playlists_analyzed}</p>
                </div>
                <div className="bg-slate-700 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Canciones únicas</p>
                  <p className="text-2xl font-bold text-white">{results.total_unique_songs}</p>
                </div>
                <div className="bg-cyan-900/30 p-4 rounded-lg border border-cyan-700" data-testid="analyzer-stat-existing">
                  <p className="text-cyan-400 text-sm">En biblioteca</p>
                  <p className="text-2xl font-bold text-cyan-400">{results.existing_count || 0}</p>
                </div>
                <div className="bg-green-900/30 p-4 rounded-lg border border-green-700">
                  <p className="text-green-400 text-sm">Actualizadas</p>
                  <p className="text-2xl font-bold text-green-400">{results.songs_updated}</p>
                </div>
                <div className="bg-purple-900/30 p-4 rounded-lg border border-purple-700">
                  <p className="text-purple-400 text-sm">Recomendadas</p>
                  <p className="text-2xl font-bold text-purple-400">{results.recommendations_count}</p>
                </div>
              </div>

              {/* Existing Songs — ya en biblioteca, mostradas siempre con occurrences.
                  Actúa como "prototipo de playlist": el DJ ve qué canciones suyas
                  aparecen en varias playlists del género y las prepara para el evento. */}
              {results.existing_songs && results.existing_songs.length > 0 && (
                <div data-testid="analyzer-existing-section">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white text-lg font-semibold flex items-center gap-2">
                      <Headphones className="w-5 h-5 text-cyan-400" />
                      En tu biblioteca ({filteredExisting.length})
                    </h3>
                  </div>

                  <div className="bg-slate-900/50 rounded-lg border border-slate-700 divide-y divide-slate-700 max-h-96 overflow-y-auto">
                    {filteredExisting.length === 0 ? (
                      <p className="text-slate-400 text-sm p-4 text-center">
                        Ninguna canción de tu biblioteca coincide con el filtro actual.
                      </p>
                    ) : filteredExisting.map((song) => (
                      <div
                        key={song.id}
                        className="flex items-center gap-3 p-3 hover:bg-slate-800/60 transition-colors"
                        data-testid={`analyzer-existing-row-${song.id}`}
                      >
                        <span
                          className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-cyan-900/50 text-cyan-300 border border-cyan-700 tabular-nums"
                          title={`Aparece en ${song.occurrences} de ${results.playlists_analyzed} playlists analizadas`}
                        >
                          {song.occurrences}× <span className="text-cyan-500">listas</span>
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{song.title}</p>
                          <p className="text-slate-400 text-sm truncate">{song.artist}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {song.rating_actual != null && (
                            <span
                              className="text-xs text-yellow-400"
                              title={`Rating actual: ${song.rating_actual}★ · Propuesto por análisis: ${song.rating_propuesto}★`}
                            >
                              {getRatingStars(song.rating_actual)}
                            </span>
                          )}
                          {song.favorito_actual && <Heart className="w-4 h-4 text-red-400 fill-red-400" />}
                          <Button
                            size="sm"
                            variant={song.pendiente_denon ? 'default' : 'outline'}
                            onClick={() => handleToggleDenonPending(song)}
                            className={song.pendiente_denon
                              ? 'bg-orange-600 hover:bg-orange-700 text-white text-xs'
                              : 'border-orange-600 text-orange-400 hover:bg-orange-900/30 text-xs'}
                            title={song.pendiente_denon
                              ? 'Desmarcar pendiente Denon'
                              : 'Marcar como pendiente de preparar en Denon'}
                            data-testid={`analyzer-denon-toggle-${song.id}`}
                          >
                            {song.pendiente_denon ? '✓ Pendiente' : 'Denon'}
                          </Button>
                          {song.spotify_url && (
                            <a
                              href={song.spotify_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-400 hover:text-green-300 text-xs underline whitespace-nowrap"
                              title="Abrir en Spotify"
                            >
                              Spotify
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {!overwriteRatings && filteredExisting.some(s => s.rating_actual != null && s.rating_actual !== s.rating_propuesto) && (
                    <p className="text-xs text-amber-400 mt-2">
                      💡 Algunas canciones ya tienen rating y no se sobreescribió. Vuelve a analizar con el checkbox
                      &quot;Sobrescribir ratings&quot; marcado si quieres que el rating refleje las ocurrencias en estas playlists.
                    </p>
                  )}

                  {/* Crear prototipo: atajo para generar un evento-plantilla
                      con las canciones de biblioteca del filtro actual. */}
                  <div
                    className="mt-4 p-3 rounded-lg border border-cyan-700/60 bg-cyan-900/20"
                    data-testid="analyzer-prototype-panel"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-xs text-cyan-300 mb-1">
                          Crear evento-plantilla con{' '}
                          <span className="font-semibold text-cyan-200" data-testid="analyzer-prototype-lib-count">
                            {filteredExisting.length}
                          </span>{' '}
                          canciones de tu biblioteca
                          {prototypeIncludeDenon && addedDenonIds.size > 0 && (
                            <>
                              {' + '}
                              <span
                                className="font-semibold text-orange-300"
                                data-testid="analyzer-prototype-denon-count"
                              >
                                {addedDenonIds.size}
                              </span>{' '}
                              pendiente{addedDenonIds.size !== 1 ? 's' : ''} Denon
                            </>
                          )}
                        </label>
                        <Input
                          type="text"
                          value={prototypeName}
                          onChange={(e) => setPrototypeName(e.target.value)}
                          placeholder="Nombre del prototipo (ej. Plantilla: Comuniones)"
                          className="bg-slate-900 border-slate-700 text-white text-sm"
                          data-testid="analyzer-prototype-name-input"
                        />
                      </div>
                      <Button
                        onClick={handleCreatePrototype}
                        disabled={
                          isCreatingPrototype ||
                          !prototypeName.trim() ||
                          (filteredExisting.length === 0 && (!prototypeIncludeDenon || addedDenonIds.size === 0))
                        }
                        className="bg-cyan-600 hover:bg-cyan-700 text-white whitespace-nowrap"
                        data-testid="analyzer-create-prototype-btn"
                      >
                        {isCreatingPrototype ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Save className="w-4 h-4 mr-1" />
                        )}
                        Crear prototipo
                      </Button>
                    </div>
                    {addedDenonIds.size > 0 && (
                      <label
                        className="mt-2 flex items-center gap-2 text-xs text-orange-300 cursor-pointer select-none"
                        data-testid="analyzer-prototype-include-denon-label"
                      >
                        <input
                          type="checkbox"
                          checked={prototypeIncludeDenon}
                          onChange={(e) => setPrototypeIncludeDenon(e.target.checked)}
                          className="accent-orange-500"
                          data-testid="analyzer-prototype-include-denon-checkbox"
                        />
                        <span>
                          Incluir también {addedDenonIds.size} canción{addedDenonIds.size !== 1 ? 'es' : ''} marcada{addedDenonIds.size !== 1 ? 's' : ''} como pendiente Denon
                          en esta sesión
                        </span>
                      </label>
                    )}
                    <p className="text-[11px] text-cyan-500/80 mt-2">
                      Se creará un evento con estas canciones en la lista personalizada.
                      Podrás clonarlo o usarlo como plantilla en eventos reales.
                    </p>
                  </div>
                </div>
              )}

              {/* Recommended Songs */}
              {results.recommended_songs && results.recommended_songs.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white text-lg font-semibold flex items-center gap-2">
                      <Music className="w-5 h-5 text-purple-400" />
                      Canciones Recomendadas ({filteredRecommendations.length})
                    </h3>
                    <select
                      value={minOccurrences}
                      onChange={(e) => {
                        setMinOccurrences(parseInt(e.target.value));
                        setSelectedSongs(new Set());
                      }}
                      className="bg-slate-700 text-white px-3 py-1 rounded border border-slate-600 text-sm"
                    >
                      <option value={1}>Todas las canciones</option>
                      <option value={2}>2+ listas</option>
                      <option value={3}>3+ listas</option>
                      <option value={4}>4+ listas</option>
                      <option value={5}>5+ listas</option>
                      <option value={6}>6+ listas</option>
                    </select>
                  </div>

                  {/* Bulk Actions */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Button
                      onClick={handleAddAllToLibrary}
                      disabled={addingAllToLibrary || filteredRecommendations.length === 0}
                      className="bg-green-600 hover:bg-green-700 text-white text-sm"
                      size="sm"
                    >
                      {addingAllToLibrary ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Plus className="w-4 h-4 mr-1" />
                      )}
                      Añadir Todas ({filteredRecommendations.length})
                    </Button>
                    <Button
                      onClick={handleAddAllToDenon}
                      disabled={addingAllToDenon || filteredRecommendations.length === 0}
                      className="bg-orange-600 hover:bg-orange-700 text-white text-sm"
                      size="sm"
                    >
                      {addingAllToDenon ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Headphones className="w-4 h-4 mr-1" />
                      )}
                      Todas a Denon ({filteredRecommendations.length})
                    </Button>
                    
                    <div className="border-l border-slate-600 mx-2"></div>
                    
                    <Button
                      onClick={selectAll}
                      variant="outline"
                      className="text-white border-slate-600 hover:bg-slate-700 text-sm"
                      size="sm"
                    >
                      Seleccionar Todas
                    </Button>
                    <Button
                      onClick={deselectAll}
                      variant="outline"
                      className="text-white border-slate-600 hover:bg-slate-700 text-sm"
                      size="sm"
                    >
                      Deseleccionar
                    </Button>
                    
                    {selectedSongs.size > 0 && (
                      <>
                        <Button
                          onClick={handleAddSelectedToLibrary}
                          className="bg-green-600 hover:bg-green-700 text-white text-sm"
                          size="sm"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Añadir Seleccionadas ({selectedSongs.size})
                        </Button>
                        <Button
                          onClick={handleAddSelectedToDenon}
                          className="bg-orange-600 hover:bg-orange-700 text-white text-sm"
                          size="sm"
                        >
                          <Headphones className="w-4 h-4 mr-1" />
                          Seleccionadas a Denon ({selectedSongs.size})
                        </Button>
                      </>
                    )}
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {filteredRecommendations.map((song, index) => (
                      <div
                        key={index}
                        className="bg-slate-700 p-4 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                      >
                        <div className="flex items-start gap-3 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedSongs.has(index)}
                            onChange={() => toggleSongSelection(index)}
                            className="w-5 h-5 rounded border-slate-600 mt-1"
                          />
                          <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-white font-medium">{song.title}</p>
                            {song.favorito && <Heart className="w-4 h-4 text-red-500 fill-red-500" />}
                          </div>
                          <p className="text-slate-400 text-sm">{song.artist}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-yellow-400 text-sm">{getRatingStars(song.rating)}</span>
                            <span className="text-slate-500 text-xs">
                              En {song.occurrences} playlist{song.occurrences > 1 ? 's' : ''}
                            </span>
                          </div>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            onClick={() => handleAddToDenon(song)}
                            disabled={addingToDenon[song.title]}
                            className="bg-orange-600 hover:bg-orange-700 text-white text-sm"
                            size="sm"
                          >
                            {addingToDenon[song.title] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Headphones className="w-4 h-4 mr-1" />
                                Pendiente Denon
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleAddToLibrary(song)}
                            disabled={addingToLibrary[song.title]}
                            className="bg-green-600 hover:bg-green-700 text-white text-sm"
                            size="sm"
                          >
                            {addingToLibrary[song.title] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-1" />
                                Añadir
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results.recommended_songs && results.recommended_songs.length === 0 && (
                <div className="text-center py-8">
                  <Music className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">
                    No se encontraron canciones nuevas para recomendar.
                    <br />
                    ¡Tu biblioteca ya tiene todas las canciones de estas playlists!
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setResults(null);
                    setPlaylistUrls('');
                  }}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white"
                >
                  Nuevo Análisis
                </Button>
                <Button
                  onClick={onClose}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
